import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom"; // Use react-router-dom for routing
import { getPosts } from "../../services/postService";
import { getCategories } from "../../services/eventService";
import imgPlaceholder from "../../assets/images/resources/img-placeholder.svg";
import { BLOG_IMAGE_POOL, pickPoolImage } from "../../utils/imagePools";

const formatShortDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

const formatLongDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
};

const BloglistMain = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const loadPosts = useCallback((page = 1) => {
    setLoading(true);
    setError(null);
    getPosts({ page })
      .then((res) => {
        setPosts(res.data || []);
        setMeta(res.meta || { current_page: 1, last_page: 1 });
      })
      .catch(() => {
        setError("Blog posts could not be loaded. Please make sure the Laravel API server is running.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPosts(1);
    getCategories()
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, [loadPosts]);

  // Recent posts = latest 3 loaded posts (fine for the sidebar, no extra request needed)
  const recentPosts = posts.slice(0, 3);

  const filteredPosts = search
    ? posts.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))
    : posts;

  return (
    <React.Fragment>
      {/* Blog List Start */}
      <section className="blog-list">
        <div className="container">
          <div className="row">
            {/* Blog List */}
            <div className="col-xl-8 col-lg-7">
              <div className="blog-list__left">
                {loading && <p>Loading posts...</p>}
                {error && <p style={{ color: "#c0392b" }}>{error}</p>}
                {!loading && !error && filteredPosts.length === 0 && (
                  <p>No published blog posts found yet.</p>
                )}

                {filteredPosts.map((post) => (
                  <div className="blog-list__single" key={post.id}>
                    <div className="blog-list__img-box">
                      <div className="blog-list__img">
                        <img
                          src={post.image || pickPoolImage(BLOG_IMAGE_POOL, post.id ?? post.slug ?? post.title) || imgPlaceholder}
                          alt={post.title}
                          onError={(e) => { e.target.onerror = null; e.target.src = imgPlaceholder; }}
                        />
                      </div>
                      <div className="blog-list__date">
                        <p>{formatShortDate(post.published_at)}</p>
                      </div>
                    </div>
                    <div className="blog-list__content">
                      <ul className="blog-list__meta list-unstyled">
                        <li>
                          <Link to="#">
                            <span className="icon-user"></span>By {post.author_name}
                          </Link>
                        </li>
                        <li>
                          <Link to="#">
                            <span className="icon-calendar"></span>
                            {formatLongDate(post.published_at)}
                          </Link>
                        </li>
                      </ul>
                      <h3 className="blog-list__title">
                        <Link to={`/blog-details/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <div className="blog-list__btn-box">
                        <Link to={`/blog-details/${post.slug}`} className="blog-list__btn thm-btn">
                          Read More
                          <span className="icon-arrow-right"></span>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {meta.last_page > 1 && (
                  <div className="blog-list__pagination">
                    <ul className="pg-pagination list-unstyled">
                      {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((p) => (
                        <li className="count" key={p}>
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              loadPosts(p);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            {String(p).padStart(2, "0")}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-xl-4 col-lg-5">
              <div className="sidebar">
                {/* Search */}
                <div className="sidebar__single sidebar__search">
                  <h3 className="sidebar__title">Search</h3>
                  <form
                    className="sidebar__search-form"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      placeholder="Search...."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit">
                      <i className="icon-loupe"></i>
                    </button>
                  </form>
                </div>

                {/* Categories */}
                <div className="sidebar__single sidebar__all-category">
                  <h3 className="sidebar__title">Category</h3>
                  <ul className="sidebar__all-category-list list-unstyled">
                    {categories.map((category) => (
                      <li key={category.id}>
                        <Link to={`/events?category=${category.slug}`}>
                          <i className="icon-double-angle"></i>
                          {category.name}
                          <span>({category.events_count})</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recent Posts */}
                <div className="sidebar__single sidebar__post">
                  <h3 className="sidebar__title">Recent Post</h3>
                  <ul className="sidebar__post-list list-unstyled">
                    {recentPosts.map((post) => (
                      <li key={post.id}>
                        <div className="sidebar__post-content">
                          <p className="sidebar__post-date">
                            <span className="icon-calendar"></span>
                            {formatShortDate(post.published_at)}
                          </p>
                          <h3>
                            <Link to={`/blog-details/${post.slug}`}>{post.title}</Link>
                          </h3>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Blog List End */}
    </React.Fragment>
  );
};

export default BloglistMain;
